/**
 * Analytics Utility
 * Google Analytics setup and conversion tracking
 * Following best practices for privacy and performance
 * Note: This version is corrected to avoid continuous requests.
 */

// Google Analytics Measurement ID from environment variables
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// Analytics utility for Google Analytics tracking

// Initialize Google Analytics with proper configuration and deferred loading
export const initGA = () => {
  if (typeof window !== 'undefined' && !window.gtag && GA_MEASUREMENT_ID) {
    // Defer GA loading until after page load to improve initial performance
    const loadGA = () => {
      // Load Google Analytics script
      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);
      
      // Remove the event listener after loading
      window.removeEventListener('load', loadGA);
    };
    
    // Load GA after window load event or after 3 seconds (whichever comes first)
    if (document.readyState === 'complete') {
      setTimeout(loadGA, 100);
    } else {
      window.addEventListener('load', loadGA);
      setTimeout(loadGA, 3000);
    }
    
    // Return early and initialize gtag when script loads
    return;
  }
  
  if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
    // GA already loaded, just configure it
    configureGA();
  }
};

// Separate configuration function
const configureGA = () => {
  if (typeof window === 'undefined' || !window.gtag) return;

  // Initialize gtag if not already initialized
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
  }

  // Configure GA with PROPER settings to prevent continuous requests
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
      // Disable Enhanced Measurement (prevents automatic events)
      enhanced_measurement: false,
      // Disable automatic page view (we'll send manually)
      send_page_view: false,
      // Disable user engagement heartbeat (main cause of continuous requests)
      engagement_time_msec: false,
      // Disable all automatic event tracking
      scroll_tracking: false,
      outbound_click_tracking: false,
      site_search_tracking: false,
      video_tracking: false,
      file_download_tracking: false,
      form_tracking: false,
      // Additional settings to prevent automatic events
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      // More aggressive settings to prevent automatic events
      custom_map: {},
      // Disable automatic event collection
      auto_enhanced_measurement: false,
      // Disable automatic page view tracking
      page_view_tracking: false,
      // Disable automatic user engagement tracking
      user_engagement_tracking: false,
      // Additional settings to completely disable automatic events
      // Disable all automatic event collection
      automatic_enhanced_measurement: false,
      // Disable user engagement tracking completely
      user_engagement: false,
      // Disable automatic event tracking
      automatic_events: false,
      // Disable all automatic tracking
      tracking: false,
  });
  
  // Send initial page view manually (since we disabled automatic page views)
  window.gtag('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
  });
  
  // Block automatic events that might still be sent
  const originalGtag = window.gtag;
  let eventCount = 0;
  const maxEvents = 5; // Allow only 5 events per minute
  
  window.gtag = function(...args: any[]) {
    // Block specific automatic events
    if (args.length > 0 && args[0] === 'event') {
      const eventName = args[1];
      const blockedEvents = [
        'user_engagement',
        'engagement_time_msec',
        'scroll',
        'click',
        'file_download',
        'form_submit',
        'video_start',
        'video_progress',
        'video_complete',
        'search',
        'view_item',
        'select_item',
        'add_to_cart',
        'remove_from_cart',
        'begin_checkout',
        'purchase',
        'refund',
        'login',
        'sign_up',
        'search',
        'view_search_results',
        'select_content',
        'share',
        'earn_virtual_currency',
        'spend_virtual_currency',
        'unlock_achievement',
        'level_up',
        'join_group',
        'add_payment_info',
        'add_shipping_info',
        'add_to_wishlist',
        'generate_lead',
        'tutorial_begin',
        'tutorial_complete',
        'view_item_list',
        'view_promotion',
        'select_promotion',
        'view_cart',
        'begin_checkout',
        'add_payment_info',
        'add_shipping_info',
        'purchase',
        'refund',
        'view_item',
        'select_item',
        'view_item_list',
        'view_promotion',
        'select_promotion',
        'view_cart',
        'begin_checkout',
        'add_payment_info',
        'add_shipping_info',
        'purchase',
        'refund'
      ];
      
      if (blockedEvents.includes(eventName)) {
        return;
      }
      
      // Rate limiting - block if too many events
      eventCount++;
      if (eventCount > maxEvents) {
        return;
      }
      
      // Reset counter every minute
      if (eventCount === 1) {
        setTimeout(() => {
          eventCount = 0;
        }, 60000);
      }
    }
    
    // Allow manual events
    return originalGtag.apply(this, args);
  };
};

// --- Custom Event Tracking Functions ---

const eventThrottle = new Map<string, number>();
const THROTTLE_DELAY = 1000; // 1 second between same events

// Track page views
export const trackPageView = (url: string, title?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
      page_title: title || document.title,
    });
  }
};

// Track custom events with throttling
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    const eventKey = `${action}_${category}_${label || ''}`;
    const now = Date.now();
    const lastSent = eventThrottle.get(eventKey);
    
    // Throttle events to prevent spam
    if (!lastSent || now - lastSent > THROTTLE_DELAY) {
      eventThrottle.set(eventKey, now);
      
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
  }
};

export const trackConversion = (conversionType: string, value?: number) => {
  trackEvent('conversion', 'engagement', conversionType, value);
};

// Track form submissions
export const trackFormSubmission = (formName: string, formType: string) => {
  trackEvent('form_submit', 'engagement', `${formName}_${formType}`);
};

// Track button clicks
export const trackButtonClick = (buttonName: string, page: string) => {
  trackEvent('button_click', 'engagement', `${buttonName}_${page}`);
};

// Track file downloads
export const trackDownload = (fileName: string, fileType: string) => {
  trackEvent('file_download', 'engagement', `${fileName}_${fileType}`);
};

export const trackChatEvent = (eventType: string, details?: string) => {
  trackEvent('chat_interaction', 'engagement', `${eventType}_${details || ''}`);
};

export const trackROICalculation = (calculationType: string) => {
  trackEvent('roi_calculation', 'tool_usage', calculationType);
};

export const trackServiceInteraction = (serviceName: string, action: string) => {
  trackEvent('service_interaction', 'engagement', `${serviceName}_${action}`);
};

// Track scroll depth
export const trackScrollDepth = (depth: number) => {
  trackEvent('scroll_depth', 'engagement', `scroll_${depth}%`);
};

// Track time on page
export const trackTimeOnPage = (timeInSeconds: number, page: string) => {
  trackEvent('time_on_page', 'engagement', `${page}_${Math.round(timeInSeconds)}s`);
};

// Track user engagement score
export const trackEngagementScore = (score: number, page: string) => {
  trackEvent('engagement_score', 'engagement', `${page}_${score}`, score);
};

// Track user journey
export const trackUserJourney = (journeyStep: string, previousStep?: string) => {
  trackEvent('user_journey', 'engagement', `${journeyStep}_${previousStep || ''}`);
};

// Performance tracking
export const trackPerformance = (metric: string, value: number) => {
  trackEvent('performance', 'technical', metric, value);
};

// Error tracking
export const trackError = (errorType: string, errorMessage: string) => {
  trackEvent('error', 'technical', `${errorType}_${errorMessage}`);
};

// Analytics tracking functions for production use

// --- Export types for TypeScript ---
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    hasTrackedPerformance?: boolean;
  }
}

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackConversion,
  trackFormSubmission,
  trackButtonClick,
  trackDownload,
  trackChatEvent,
  trackROICalculation,
  trackServiceInteraction,
  trackScrollDepth,
  trackTimeOnPage,
  trackEngagementScore,
  trackUserJourney,
  trackPerformance,
  trackError,
};
